import Avatar from "@material-ui/core/Avatar";
import Container from "@material-ui/core/Container";
import Divider from "@material-ui/core/Divider";
import Paper from "@material-ui/core/Paper";
import type { Theme } from "@material-ui/core/styles";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import { format } from "date-fns";
import ja from "date-fns/locale/ja";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ProviderIcon from "../../components/ProviderIcon";
import { getURL } from "../../models/media";
import { getUser, UserDetail } from "../../models/user";
import Comment from "./Comment";
import OrderList from "./OrderList";
import SubscriptionList from "./SubscriptionList";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    "& > *": {
      marginTop: theme.spacing(4),
    },
  },
  profileHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(3),
    padding: theme.spacing(3),
  },
  avatar: {
    width: theme.spacing(12),
    height: theme.spacing(12),
    flexShrink: 0,
  },
  profileSummary: {
    minWidth: 0,
  },
  profileName: {
    fontWeight: theme.typography.fontWeightBold,
  },
  profileSub: {
    marginTop: theme.spacing(0.5),
  },
  profileMeta: {
    marginTop: theme.spacing(1),
  },
}));

function formatName(user: UserDetail): string {
  const name = [user.lastName, user.firstName].filter(Boolean).join(" ");
  return name || "-";
}

function formatGender(gender?: string): string {
  if (gender === "male") {
    return "男";
  }
  if (gender === "female") {
    return "女";
  }
  return "-";
}

function formatAge(birthYear?: number): string {
  if (!birthYear) {
    return "-";
  }
  const age = new Date().getFullYear() - birthYear;
  if (age <= 0) {
    return "-";
  }
  return `${age}歳`;
}

function formatProfileMeta(user: UserDetail): string {
  return [
    user.prefecture || "",
    formatAge(user.birthYear),
    formatGender(user.gender),
  ]
    .filter((x) => x && x !== "-")
    .join("・") || "-";
}

export default function UserView() {
  const classes = useStyles();

  const { uid } = useParams<{
    uid?: string;
  }>();

  const [user, setUser] = useState<UserDetail>();
  useEffect(() => {
    if (!uid) return;
    getUser(uid).then(setUser, console.error);
  }, [uid]);

  if (!user) return null;

  return (
    <Container className={classes.root}>
      <section id="customer">
        <Typography variant="h2" component="h1" gutterBottom>
          顧客
        </Typography>
        <Paper>
          <div className={classes.profileHeader}>
            <Avatar
              className={classes.avatar}
              src={user.avatar ? getURL(user.avatar) : undefined}
            />
            <div className={classes.profileSummary}>
              <Typography
                className={classes.profileName}
                component="h2"
                variant="h4"
              >
                {formatName(user)}
              </Typography>
              <Typography
                className={classes.profileSub}
                color="textSecondary"
                variant="h6"
              >
                ({user.nickname || "-"})
              </Typography>
              <Typography
                className={classes.profileMeta}
                color="textSecondary"
                variant="body1"
              >
                {formatProfileMeta(user)}
              </Typography>
            </div>
          </div>
          <Divider />
          <Table>
            <TableBody>
              <TableRow>
                <TableCell component="th" scope="row">
                  UID
                </TableCell>
                <TableCell align="right">{user.uid}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  メールアドレス
                </TableCell>
                <TableCell align="right">{user.email || "-"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  プロバイダ
                </TableCell>
                <TableCell align="right">
                  {Array.from(new Set(user.providers)).map((p) => (
                    <ProviderIcon key={p} provider={p} />
                  ))}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  アカウント作成日時
                </TableCell>
                <TableCell align="right">
                  {format(
                    user.userMetadata.creationTime,
                    "yyyy-MM-dd HH:mm:ss",
                    {
                      locale: ja,
                    }
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  最終ログイン日時
                </TableCell>
                <TableCell align="right">
                  {format(
                    user.userMetadata.lastLogInTime,
                    "yyyy-MM-dd HH:mm:ss",
                    {
                      locale: ja,
                    }
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      </section>
      <section id="subscriptions">
        <Typography variant="h2" gutterBottom>
          サブスクリプション
        </Typography>
        <SubscriptionList uid={user.uid} />
      </section>
      <section id="orders">
        <Typography variant="h2" gutterBottom>
          購入履歴
        </Typography>
        <OrderList uid={user.uid} />
      </section>
      <section id="comments">
        <Typography variant="h2" gutterBottom>
          コメント
        </Typography>
        <Comment uid={user.uid} />
      </section>
    </Container>
  );
}
